import api from "@/lib/api";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function badgeSupported() {
  return typeof navigator !== "undefined" && "setAppBadge" in navigator;
}

export async function getPushState() {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) return "subscribed";
    return Notification.permission === "granted" ? "granted" : "default";
  } catch {
    return "default";
  }
}

export async function enableAdminPush() {
  if (!pushSupported()) throw new Error("Push notifications are not supported on this browser.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error(perm === "denied"
      ? "Notifications are blocked. Enable them in your browser/site settings, then try again."
      : "Permission was not granted.");
  }
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public");
  if (!data.public_key) throw new Error("Push is not configured on the server.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(data.public_key),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return true;
}

export async function disableAdminPush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
      await sub.unsubscribe();
    }
  } catch { /* ignore */ }
}

export async function updateAppBadge() {
  if (!badgeSupported()) return;
  try {
    const { data } = await api.get("/admin/notifications/count");
    if (data.total > 0) navigator.setAppBadge(data.total);
    else navigator.clearAppBadge();
  } catch { /* ignore */ }
}

export const RUNNER_CATEGORIES = [
  { slug: "running", name: "Running" },
  { slug: "trail-running", name: "Trail Running" },
  { slug: "cycling", name: "Cycling" },
  { slug: "triathlon", name: "Triathlon" },
  { slug: "duathlon", name: "Duathlon" },
];

async function subscribeRaw() {
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public");
  if (!data.public_key) throw new Error("Push is not configured on the server.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(data.public_key) });
  return sub.toJSON();
}

export async function enableRunnerPush(sports) {
  if (!pushSupported()) throw new Error("Push notifications are not supported on this browser.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error(perm === "denied"
      ? "Notifications are blocked. Enable them in your browser/site settings, then try again."
      : "Permission was not granted.");
  }
  const subscription = await subscribeRaw();
  await api.post("/runner/notifications/subscribe", { subscription, sports });
  return true;
}

export async function getRunnerNotifications() {
  const { data } = await api.get("/runner/notifications");
  return data; // { sports, subscribed }
}

export async function saveRunnerSports(sports) {
  await api.put("/runner/notifications", { sports });
}

export async function disableRunnerPush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) { await api.post("/runner/notifications/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
  } catch { /* ignore */ }
}
